/**
 * Minimal word-level diff behind the assistant's text-change affordances.
 *
 * Tool steps that update text carry the block's plaintext before and after
 * the step. This module turns those two strings into presentation-ready
 * pieces: character ranges in the new text (for the canvas flash on the
 * changed words) and a compact same/del/ins excerpt (for the activity rail).
 */

// Words keep their trailing whitespace, so joining tokens round-trips to
// the original string and character offsets stay exact.
const tokenize = (text) => text.match(/\s+|\S+\s*/g) || [];

// Above this many table cells the LCS is not worth the work — treat the
// change as a full replacement instead.
const LCS_CELL_CAP = 250000;

// Unchanged runs of at most this many tokens between two changes get
// folded into the change. Without this, a large rewrite that happens to
// share scattered words with the original renders as an unreadable
// del/ins zigzag instead of one clean replacement.
const BRIDGE_MAX = 2;

const coalesce = (ops) => {
  const out = [];
  // Open replacement region: del/ins text accumulated in document order.
  let region = null;
  const flush = () => {
    if (!region) return;
    if (region.del)
      out.push({ type: 'del', text: region.del, tokens: [region.del] });
    if (region.ins)
      out.push({ type: 'ins', text: region.ins, tokens: [region.ins] });
    region = null;
  };
  ops.forEach((op, index) => {
    if (op.type === 'same') {
      const next = ops[index + 1];
      if (region && op.tokens.length <= BRIDGE_MAX && next) {
        // Short bridge between two changes: belongs to both sides.
        region.del += op.text;
        region.ins += op.text;
      } else {
        flush();
        out.push(op);
      }
      return;
    }
    if (!region) region = { del: '', ins: '' };
    region[op.type] += op.text;
  });
  flush();
  return out;
};

/**
 * Word-level diff of two strings. Returns merged runs
 * [{ type: 'same' | 'del' | 'ins', text, tokens }] in document order,
 * with trivially short unchanged runs folded into their surrounding
 * change.
 */
export const diffWords = (before, after) => {
  const a = tokenize(before);
  const b = tokenize(after);
  const push = (ops, type, token) => {
    const last = ops[ops.length - 1];
    if (last && last.type === type) {
      last.tokens.push(token);
      last.text += token;
    } else {
      ops.push({ type, text: token, tokens: [token] });
    }
  };
  if (a.length * b.length > LCS_CELL_CAP) {
    const ops = [];
    if (before) push(ops, 'del', before);
    if (after) push(ops, 'ins', after);
    return ops;
  }
  // Tokens match on their word, not their whitespace — otherwise a word
  // that merely gained or lost a trailing space shows up as changed.
  // 'same' runs keep the after-side text, so offsets into the new string
  // stay exact.
  const na = a.map((token) => token.replace(/\s+$/, ''));
  const nb = b.map((token) => token.replace(/\s+$/, ''));
  const table = Array.from({ length: a.length + 1 }, () =>
    new Array(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i][j] =
        na[i] === nb[j]
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (na[i] === nb[j]) {
      push(ops, 'same', b[j]);
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      push(ops, 'del', a[i]);
      i += 1;
    } else {
      push(ops, 'ins', b[j]);
      j += 1;
    }
  }
  while (i < a.length) push(ops, 'del', a[i++]);
  while (j < b.length) push(ops, 'ins', b[j++]);
  const merged = coalesce(ops);
  // A deletion's separating whitespace lives on the after-side tokens, so
  // a trailing deletion can end up glued onto the kept text ("worldextra").
  // Give it back a separator — offsets are unaffected, deleted text does
  // not exist in the new string.
  merged.forEach((op, index) => {
    if (
      op.type === 'del' &&
      index > 0 &&
      !/^\s/.test(op.text) &&
      !/\s$/.test(merged[index - 1].text)
    ) {
      op.text = ` ${op.text}`;
      op.tokens = [op.text];
    }
  });
  return merged;
};

/**
 * Character ranges of the inserted runs, as offsets into the new string
 * (trailing whitespace excluded so highlights hug the words).
 */
export const changedRanges = (ops) => {
  const ranges = [];
  let offset = 0;
  ops.forEach((op) => {
    if (op.type === 'del') return;
    if (op.type === 'ins') {
      const marked = op.text.replace(/\s+$/, '');
      if (marked) ranges.push({ start: offset, end: offset + marked.length });
    }
    offset += op.text.length;
  });
  return ranges;
};

// Unchanged tokens kept around each change in the rail excerpt. The
// excerpt is only a teaser — the full change is one click away.
const CONTEXT = 1;
// Longest del/ins segment shown before it gets cut off.
const SEGMENT_CAP = 30;

const clip = (text) => {
  if (text.length <= SEGMENT_CAP) return text;
  let cut = text.slice(0, SEGMENT_CAP - 1);
  // Never split a surrogate pair — a lone half renders as mojibake.
  if (/[\uD800-\uDBFF]$/.test(cut)) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}…`;
};

/**
 * Compact excerpt of the change for the activity rail: the del/ins runs
 * with a little unchanged context, long stretches elided with ellipses.
 * Returns [{ type, text }] or null when the strings are equal.
 */
export const diffExcerpt = (before, after) => {
  const ops = diffWords(before, after);
  if (!ops.some((op) => op.type !== 'same')) return null;
  const parts = [];
  let marks = 0;
  ops.forEach((op, index) => {
    if (op.type !== 'same') {
      // Whitespace between tokens stays outside the mark, so trimmed
      // del/ins runs do not glue onto their neighbours — neither onto
      // another mark nor onto unchanged text that carries no whitespace
      // of its own.
      const marked = op.text.trim();
      if (marked) {
        const last = parts[parts.length - 1];
        if (last && !/\s$/.test(last.text)) {
          parts.push({ type: 'same', text: ' ' });
        }
        parts.push({ type: op.type, text: clip(marked) });
        marks += 1;
      }
      if (/\s$/.test(op.text)) {
        parts.push({ type: 'same', text: ' ' });
      }
      return;
    }
    const { tokens } = op;
    const head = index === 0;
    const tail = index === ops.length - 1;
    if (head && tokens.length > CONTEXT) {
      parts.push({
        type: 'same',
        text: `…${tokens.slice(-CONTEXT).join('')}`,
      });
    } else if (tail && tokens.length > CONTEXT) {
      parts.push({
        type: 'same',
        text: `${tokens.slice(0, CONTEXT).join('').trimEnd()}…`,
      });
    } else if (!head && !tail && tokens.length > CONTEXT * 2 + 1) {
      parts.push({
        type: 'same',
        text: `${tokens.slice(0, CONTEXT).join('')}… ${tokens
          .slice(-CONTEXT)
          .join('')}`,
      });
    } else {
      parts.push({ type: 'same', text: op.text });
    }
  });
  // A change made purely of whitespace has nothing to mark — an excerpt
  // without a single del/ins would claim a change it cannot show.
  return marks > 0 ? parts : null;
};
